import { avatarDefault } from '@/assets/images';
import type { FamilyActivity } from '@/services/get-family-activities';
import { DAY_MS, HOUR_MS, MINUTE_MS, MONTH_MS } from './constants';
import type { FamilyActivityItem } from './types';

export function getActivityTimeMs(item: FamilyActivityItem) {
  return new Date(item.createdAt).getTime();
}

export function sortFamilyActivityItems(items: FamilyActivityItem[]) {
  return [...items].sort((a, b) => getActivityTimeMs(b) - getActivityTimeMs(a));
}

export function formatActivityTime(createdAt: string) {
  const activityTime = new Date(createdAt).getTime();
  const diff = Date.now() - activityTime;

  // Future timestamp (clock skew / bad data) — not a valid "time ago", so don't clamp
  // it into "Now"; render the placeholder instead.
  if (diff < 0) return '--';

  if (diff < MINUTE_MS) return 'Now';
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`;
  if (diff < MONTH_MS) return `${Math.floor(diff / DAY_MS)}d`;

  return new Date(createdAt).toLocaleString('en-US', { month: 'short' });
}

/** Maps an API `FamilyActivity` to the row's view model. */
export function toFamilyActivityItem(activity: FamilyActivity): FamilyActivityItem {
  return {
    id: activity.id,
    actorName: activity.actor.isSelf ? 'You' : activity.actor.name,
    actorAvatar: activity.actor.avatarUrl ? { uri: activity.actor.avatarUrl } : avatarDefault,
    playerName: activity.player?.name ?? null,
    description: activity.description,
    createdAt: activity.occurredAt,
  };
}

export type ActivityDescriptionSegment = { text: string; bold: boolean };

/**
 * Splits a server-rendered description into segments, marking occurrences of the
 * given tokens (actor + player names) as bold so the row can emphasize them
 * without re-templating the sentence client-side.
 */
export function buildActivityDescriptionSegments(
  description: string,
  boldTokens: (string | null | undefined)[],
): ActivityDescriptionSegment[] {
  const tokens = Array.from(new Set(boldTokens.filter((t): t is string => Boolean(t && t.trim()))));
  if (tokens.length === 0) return [{ text: description, bold: false }];

  const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'g');
  return description
    .split(pattern)
    .filter(part => part.length > 0)
    .map(part => ({ text: part, bold: tokens.includes(part) }));
}
